// 전자결재 API URL 상수
import { IS_APPROVAL_SEARCH_V2_ENABLED } from '@/constant';
import type {
  AttachmentUploadVariant,
  AttachmentUploadVariantConfig,
} from '@/types/approval';

// 신규 API
export const APPROVAL_AUTHOR_ME_URL = '/api/v1/approval/author/me';

export const APPROVAL_CONFIRM_ME_URL = '/api/v1/approval/confirm/me';

export const APPROVAL_UNREADREFEEREE_ME_URL =
  '/api/v1/approval/unreadReferee/me';

// 코멘트 멘션 권한체크 (미권한자 → 자동 참조인 등록) — v2 GET
// GET /api/v2/approval/{documentId}/permissions?passportIds[]=a&passportIds[]=b
// 백엔드는 배열 형식(passportIds[])만 허용 — 콤마조인은 422("배열이어야 합니다").
export const getApprovalPermissionsUrl = (
  documentId: string,
  passportIds: string[],
) => {
  const query = passportIds
    .map((id) => `passportIds[]=${encodeURIComponent(id)}`)
    .join('&');
  return `/api/v2/approval/${documentId}/permissions?${query}`;
};
// 참조인 사후 추가 (v2) — body { passportIds, comment }
export const APPROVAL_REFERRERS_URL = (documentId: string) =>
  `/api/v2/approval/${documentId}/referrers`;

// 레거시 API
export const LEGACY_APPROVAL_ME = '/api/v1/legacy-approval/approver/me';

export const LEGACY_APPROVAL_AUTHOR_URL = '/api/v1/legacy-approval/author/me';

// 작성자 본인 문서의 완료(C)/반려(R) 카운트 — SNB '완료/반려' 뱃지용.
// 자가결재(작성자=최종결재자) 문서는 서버에서 제외되어 내려온다(PORTAL-12516).
export const LEGACY_APPROVAL_AUTHOR_STATUS_BADGE_URL =
  '/api/v1/legacy-approval/author/me/status-badge';

export const LEGACY_APPROVAL_REFERRER_URL =
  '/api/v1/legacy-approval/referrer/me';

export const LEGACY_APPROVAL_TEAM_AUTHOR_URL =
  '/api/v1/legacy-approval/author/my-department';

export const LEGACY_APPROVAL_TEAM_APPROVAL_URL =
  '/api/v1/legacy-approval/approver/my-department';

export const APPROVAL_TYPE_ALL_URL = '/api/v1/legacy-approval/type/all';

// 작성 화면 양식 선택 목록 — V2 네이티브. BE 가 작성자 소속 법인(ACL) 기준으로 노출 양식을 필터해서 내려준다(빈 acl=전체 노출). PORTAL-11920.
export const APPROVAL_TYPE_V2_LIST_URL = '/api/v2/approval/type';

/**
 * 양식 메타 단건 — V2. 고정 참조인/고정 협조결재자/기본 참조인은 BE 가 요청자 소속 법인
 * 기준으로 좁힌 flat 배열로 내려주며 퇴사자는 제외된다(PORTAL-13902). v1 legacy 는
 * 그룹 구조 + 퇴사자 미필터라 더 이상 쓰지 않는다.
 */
export const getApprovalTypeUrl = (code: number) =>
  `${APPROVAL_V2_URL}/type/${code}`;

/**
 * 계약 품의(117·118) 작성 사전 데이터 — 법틀 계약 목록(PORTAL-14185).
 * contractIds 생략 → 작성자 사번 기준 선택 가능 계약 전건(페이징 없음).
 * 지정 → 그 계약만 요청 순서대로 + attributes.contractIds 채워짐(선택 확정·조회 재조회용).
 * 배열은 `contractIds[]=` 반복 형식만 허용 — 콤마조인은 permissions 와 같이 422.
 * 에러: 404=접근 불가 계약 포함(전체 거절) / 422=사번 없음·결합값 100자 초과 / 424=법틀 장애.
 * partial → 보는 사람 권한 기준 계약별 결과(display.contractResults)로 받음 — 404 로 전체 거절 안 함. 조회 화면용.
 * partial 은 1/0 만 받음(true 는 422)
 */
export const getContractDraftSourceUrl = (
  code: number,
  contractIds?: number[],
  { partial = false }: { partial?: boolean } = {},
) => {
  const base = `${APPROVAL_V2_URL}/type/${code}/draft-source`;
  if (!contractIds?.length) return base;
  const query = contractIds.map((id) => `contractIds[]=${id}`).join('&');
  return `${base}?${query}${partial ? '&partial=1' : ''}`;
};

/**
 * SA 전표(99) 작성 사전 데이터 — SAP 묶음번호로 전표 합계·라인 조회.
 * 에러: 404=SAP 에 없는 묶음번호 / 422=묶음번호 누락 / 408·424=SAP 장애
 */
export const getSaVoucherDraftSourceUrl = (sapDocumentNo: string) =>
  `${APPROVAL_V2_URL}/type/99/draft-source?sapDocumentNumber=${encodeURIComponent(
    sapDocumentNo,
  )}`;

/**
 * 할인권 발행(68) 작성 사전 데이터 — 브랜드·기간으로 할인권 목록, voucherNumber 를 주면 조건·대상 품목까지.
 * 에러: 422=파라미터 오류 / 408·424=EAI 장애
 */
export const getDiscountVoucherDraftSourceUrl = (params: {
  brandCode: string;
  openDate?: string;
  dueDate?: string;
  voucherNumber?: string;
}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) query.set(key, value);
  });
  return `${APPROVAL_V2_URL}/type/68/draft-source?${query.toString()}`;
};

/**
 * SAP 중간 관리 수수료 정산(157) 작성 사전 데이터 — display.source 가 SAP 원문({ HEAD, ITEM }).
 * 데이터 없는 연월도 200(ITEM 빈 배열). 에러: 404=미노출 유형 / 422=조건 누락 / 424=SAP 장애
 */
export const getShopManagerFeeDraftSourceUrl = (
  brand: string,
  year: string,
  month: string,
) =>
  `${APPROVAL_V2_URL}/type/157/draft-source?${new URLSearchParams({
    searchBrand: brand,
    searchYear: year,
    searchMonth: month,
  }).toString()}`;

/**
 * SERP 재고실사 조정(169) 작성 사전 데이터 — typeCode 는 SERP 문서 종류(품의 유형 코드 아님).
 * display.source 가 EAI 원문. 에러: 422=파라미터 누락·길이 초과 / 424=EAI 조회 실패(없는 문서 포함)
 */
export const getSerpStockAdjustmentDraftSourceUrl = (
  typeCode: string,
  sequence: string,
) =>
  `${APPROVAL_V2_URL}/type/169/draft-source?${new URLSearchParams({
    typeCode,
    sequence,
  }).toString()}`;

/** SERP 매장 변경(162) 작성 사전 데이터 — 파라미터·에러는 169 와 같음 */
export const getSerpStoreChangeDraftSourceUrl = (
  typeCode: string,
  sequence: string,
) =>
  `${APPROVAL_V2_URL}/type/162/draft-source?${new URLSearchParams({
    typeCode,
    sequence,
  }).toString()}`;

/** SERP 매장반품 요청(171) 작성 사전 데이터 — 파라미터·에러는 169 와 같음. display.source 는 반품 상세(요약 아님) */
export const getSerpStoreReturnDraftSourceUrl = (
  typeCode: string,
  sequence: string,
) =>
  `${APPROVAL_V2_URL}/type/171/draft-source?${new URLSearchParams({
    typeCode,
    sequence,
  }).toString()}`;

/** SERP 시즌운영계획(159) 작성 사전 데이터 — 파라미터·에러는 169 와 같음 */
export const getSerpSeasonPlanDraftSourceUrl = (
  typeCode: string,
  sequence: string,
) =>
  `${APPROVAL_V2_URL}/type/159/draft-source?${new URLSearchParams({
    typeCode,
    sequence,
  }).toString()}`;

/** SERP 여신유예신청(163) 작성 사전 데이터 — 파라미터·에러는 169 와 같음 */
export const getSerpLendingDelayDraftSourceUrl = (
  typeCode: string,
  sequence: string,
) =>
  `${APPROVAL_V2_URL}/type/163/draft-source?${new URLSearchParams({
    typeCode,
    sequence,
  }).toString()}`;

/** SERP 폐점 정산(181) 작성 사전 데이터 — 파라미터·에러는 169 와 같음 */
export const getSerpStoreClosureDraftSourceUrl = (
  typeCode: string,
  sequence: string,
) =>
  `${APPROVAL_V2_URL}/type/181/draft-source?${new URLSearchParams({
    typeCode,
    sequence,
  }).toString()}`;

/** SERP 재고실사 조정(다중)(210) 작성 사전 데이터 — sequence 는 매장별 문서 번호를 쉼표로 이은 값. 응답 attributes 는 typeCode 만 */
export const getSerpBulkStockAdjustmentDraftSourceUrl = (
  typeCode: string,
  sequence: string,
) =>
  `${APPROVAL_V2_URL}/type/210/draft-source?${new URLSearchParams({
    typeCode,
    sequence,
  }).toString()}`;

/** SERP 시즌마감 조정(174) 작성 사전 데이터 — 본문은 진입 문서 종류, 정산내역은 EM203 으로 같은 번호 조회 */
export const getSerpSeasonEndDraftSourceUrl = (
  typeCode: string,
  sequence: string,
) =>
  `${APPROVAL_V2_URL}/type/174/draft-source?${new URLSearchParams({
    typeCode,
    sequence,
  }).toString()}`;

/** SERP 중간관리자 교체 정산(182) 작성 사전 데이터 — 파라미터·에러는 169 와 같음 */
export const getSerpIntermediaryReplaceDraftSourceUrl = (
  typeCode: string,
  sequence: string,
) =>
  `${APPROVAL_V2_URL}/type/182/draft-source?${new URLSearchParams({
    typeCode,
    sequence,
  }).toString()}`;

/** SERP AP 재고 이관 요청(170) 작성 사전 데이터 — 파라미터·에러는 169 와 같음 */
export const getSerpApStockTransferDraftSourceUrl = (
  typeCode: string,
  sequence: string,
) =>
  `${APPROVAL_V2_URL}/type/170/draft-source?${new URLSearchParams({
    typeCode,
    sequence,
  }).toString()}`;

/** SERP 판매가 조정(160) 작성 사전 데이터 — 파라미터·에러는 169 와 같음 */
export const getSerpSellingPriceDraftSourceUrl = (
  typeCode: string,
  sequence: string,
) =>
  `${APPROVAL_V2_URL}/type/160/draft-source?${new URLSearchParams({
    typeCode,
    sequence,
  }).toString()}`;

/** SERP 매장 개점(161) 작성 사전 데이터 — 파라미터·에러는 169 와 같음 */
export const getSerpStoreOpenDraftSourceUrl = (
  typeCode: string,
  sequence: string,
) =>
  `${APPROVAL_V2_URL}/type/161/draft-source?${new URLSearchParams({
    typeCode,
    sequence,
  }).toString()}`;

/**
 * SAP 도급비 수수료 정산(167) 작성 사전 데이터 — attributes 에 year·month·data(SAP 원문 { HEAD, ITEM }).
 * 데이터 없는 연월도 200(ITEM 빈 배열). 에러: 404=미노출 유형 / 422=조건 누락 / 424=SAP 장애
 */
export const getSapContractFeeDraftSourceUrl = (
  brand: string,
  year: string,
  month: string,
) =>
  `${APPROVAL_V2_URL}/type/167/draft-source?${new URLSearchParams({
    searchBrand: brand,
    searchYear: year,
    searchMonth: month,
  }).toString()}`;

/** Sales 브랜드 권한 변경(215) — 파라미터 없이 작성자 기준 전체 브랜드·보유 여부(granted). 422=포털 유저 정보 없음 */
export const getSalesBrandPermissionDraftSourceUrl = () =>
  `${APPROVAL_V2_URL}/type/215/draft-source`;

/**
 * 법인카드 한도증액(206)·재발급(207) — 파라미터 없이 작성자 사번의 SAP 카드 마스터(display.cards, 한도 만원).
 * 에러: 408=SAP 시간 초과 / 422=SAP 미지원 법인·인사 정보 없음 / 424=SAP 조회 실패
 */
export const getCorporateCardDraftSourceUrl = (apvType: number) =>
  `${APPROVAL_V2_URL}/type/${apvType}/draft-source`;

/**
 * 연차휴가(32) — 파라미터 없으면 작성자 휴가 사용현황(display.leaveBalance, 부여 이력 없으면 null).
 * 기간 네 값을 주면 display.useCount(워킹데이 기준 사용일수)도 옴. 네 값은 모두 주거나 모두 생략.
 * 에러: 422=사번 없음·기간 형식 오류 / 424=HRIS 계산 실패
 */
export const getAnnualLeaveRequestDraftSourceUrl = (
  params?:
    | {
        /** 휴가 사용 현황 연도. 생략하면 올해, 미래 연도는 서버가 올해로 */
        year?: number;
      }
    | {
        startsAt: string;
        startsUnit: string;
        endsAt: string;
        endsUnit: string;
      },
) => {
  const base = `${APPROVAL_V2_URL}/type/32/draft-source`;
  const query = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== '') query.set(key, String(value));
  });
  const qs = query.toString();
  return qs ? `${base}?${qs}` : base;
};

/**
 * 장기근속 포상휴가(214·240) — 기간으로 display.useCount(워킹데이 기준 사용일수). [1주년] 은 부르지 않음.
 * 에러: 422=사번 없음·기간 형식 오류 / 424=HRIS 계산 실패
 */
export const getLongServiceLeaveDraftSourceUrl = (
  apvType: number,
  params: { startsAt: string; endsAt: string },
) =>
  `${APPROVAL_V2_URL}/type/${apvType}/draft-source?${new URLSearchParams(
    params,
  ).toString()}`;

/** 법인카드 품의 카드사 메일 —GET 발송 이력(hasSent·lastSentAt) / POST 발송. 403=발송 권한 없음 */
export const getCardCompanyMailUrl = (documentId: string) =>
  `${APPROVAL_V2_URL}/${documentId}/card-company-mail`;

/**
 * 수습평가서(188) 대상자 인사정보 — 사내 ID 로 조회. 대상자가 없으면 200 + target null.
 * 에러: 422=userId 누락·형식 / 424=ERP 조회 장애
 */
export const getProbationDraftSourceUrl = (userId: string) =>
  `${APPROVAL_V2_URL}/type/188/draft-source?userId=${encodeURIComponent(
    userId,
  )}`;

/**
 * 단체판매 진행(61) 작성 사전 데이터 — 브랜드·매장·기간으로 단체판매 후보, sequence 를 주면 그 건의 상세(summary)까지.
 * searchType '1' 조회 / '2' 전체조회(매장·기간 무시). 에러: 422=brandCode 누락·sequence 만 있고 shopCode 없음 / 424=EAI 장애
 */
export const getGroupSalesDraftSourceUrl = (params: {
  brandCode: string;
  shopCode?: string;
  searchType?: '1' | '2';
  fromDate?: string;
  toDate?: string;
  sequence?: string | number;
}) => {
  const query = new URLSearchParams({ brandCode: params.brandCode });
  if (params.shopCode) query.set('shopCode', params.shopCode);
  if (params.searchType) query.set('searchType', params.searchType);
  if (params.fromDate) query.set('fromDate', params.fromDate);
  if (params.toDate) query.set('toDate', params.toDate);
  if (params.sequence !== undefined && params.sequence !== '')
    query.set('sequence', String(params.sequence));
  return `${APPROVAL_V2_URL}/type/61/draft-source?${query.toString()}`;
};

/**
 * 단체판매 진행(다중)(131) 작성 사전 데이터 — 브랜드·매장·기간으로 후보, items 를 주면 그 건들의 상세(display.items)까지.
 * items 를 보내면 saleType 필수(없으면 422), 최대 50건. 조회('1')는 기간이 없으면 EAI 500. 고른 건 중 하나라도 EAI 오류면 전체 500
 */
export const getGroupSalesMultiDraftSourceUrl = (params: {
  brandCode: string;
  shopCode?: string;
  searchType?: '1' | '2';
  fromDate?: string;
  toDate?: string;
  saleType?: '금액' | '기간';
  items?: { shopCode: string; sequence: string }[];
}) => {
  const query = new URLSearchParams({ brandCode: params.brandCode });
  if (params.shopCode) query.set('shopCode', params.shopCode);
  if (params.searchType) query.set('searchType', params.searchType);
  if (params.fromDate) query.set('fromDate', params.fromDate);
  if (params.toDate) query.set('toDate', params.toDate);
  if (params.saleType) query.set('saleType', params.saleType);
  params.items?.forEach((item, i) => {
    query.set(`items[${i}][shopCode]`, item.shopCode);
    query.set(`items[${i}][sequence]`, item.sequence);
  });
  return `${APPROVAL_V2_URL}/type/131/draft-source?${query.toString()}`;
};

/**
 * SAP 채권현황(185) 작성 사전 데이터 — 연·월·브랜드로 SAP 채권현황 조회. 응답까지 20초 안팎.
 * 에러: 404=없는/미노출 유형 / 422=파라미터 오류 / 424=SAP 조회 실패(데이터 없는 기간 포함)
 */
export const getSapBondStatusDraftSourceUrl = (params: {
  year: number;
  month: number;
  brandCode: string;
}) =>
  `${APPROVAL_V2_URL}/type/185/draft-source?${new URLSearchParams({
    searchYear: String(params.year),
    searchMonth: String(params.month),
    brandCode: params.brandCode,
  }).toString()}`;

/**
 * 생산비 마감(회계)(216) 작성 사전 데이터 — 마감 월(YYYY-MM)로 SAP 생산비 집계.
 * 에러: 422=형식 오류 / 424=SAP 조회 실패. 집계 없으면 200 + 결재묶음번호 null·금액 0
 */
export const getProductionCostDraftSourceUrl = (closingMonth: string) =>
  `${APPROVAL_V2_URL}/type/216/draft-source?${new URLSearchParams({
    closingMonth,
  }).toString()}`;

/**
 * 외담대 발행(217) 작성 사전 데이터 — 구매카드 발급일(YYYY-MM-DD)로 SAP 발행내역.
 * 에러: 422=형식 오류 / 424=SAP 조회 실패. 0건이면 200 + items 빈 목록·결재묶음번호 null.
 * SAP 가 부를 때마다 결재묶음번호를 새로 발급함
 */
export const getExternalLoanDraftSourceUrl = (issueDate: string) =>
  `${APPROVAL_V2_URL}/type/217/draft-source?${new URLSearchParams({
    issueDate,
  }).toString()}`;

/**
 * SA 신규 출장비 정산(177)·사전 신청(179) 작성 사전 데이터 — base64 문서번호·출장 구분으로 SAP TravelHeaderSet 원문(display.source).
 * 스웨거는 평문이라 적혀 있지만 dev SAP 는 평문으로 조회하면 늘 빈 배열.
 * 없는 문서도 200(source 빈 배열). 에러: 404=미노출 유형 / 422=파라미터 누락 / 424=SAP 조회 실패
 */
export const getSaTravelExpenseDraftSourceUrl = (
  apvType: number,
  documentNumber: string,
  zType: string,
) =>
  `${APPROVAL_V2_URL}/type/${apvType}/draft-source?${new URLSearchParams({
    documentNumber,
    zType,
  }).toString()}`;

/**
 * 가격 등록 공급가·할인율·판매가 요청(197~199) 작성 사전 데이터 — 요청번호로 SAP 가격 등록 요청(HEAD·ITEM) 조회. 응답까지 10초 안팎.
 * 에러: 404=SAP 에 없는 요청번호 / 408=SAP 시간 초과 / 422=요청번호 누락·길이 초과 / 424=SAP 응답 해석 실패
 */
export const getFncoPriceSupplyDraftSourceUrl = (
  apvType: number,
  requestNumber: string,
) =>
  `${APPROVAL_V2_URL}/type/${apvType}/draft-source?${new URLSearchParams({
    requestNumber,
  }).toString()}`;

/**
 * 회계거래처 신규/변경(101) 작성 사전 데이터 — SAP 묶음번호로 회사명·거래처 표. display.messages 가 있으면 SAP 에서 이미 처리된 건.
 * 에러: 404=SAP 에 없는 묶음번호 / 422=sapDocumentNumber 누락
 */
export const getAccountingBpDraftSourceUrl = (sapDocumentNumber: string) =>
  `${APPROVAL_V2_URL}/type/101/draft-source?${new URLSearchParams({
    sapDocumentNumber,
  }).toString()}`;

/**
 * SAP 담보 신규(154)·변경(155) 작성 사전 데이터 — sapKey 로 SAP 매장 여신 조회. display.source = 154 [신규], 155 [변경 전, 변경 후].
 * 에러: 422=sapKey 누락·길이 초과 / 424=SAP 조회 실패
 */
export const getSapCollateralDraftSourceUrl = (
  apvType: number,
  sapKey: string,
) =>
  `${APPROVAL_V2_URL}/type/${apvType}/draft-source?${new URLSearchParams({
    sapKey,
  }).toString()}`;

/**
 * 인플루언서 월마감 보고서(108) 작성 사전 데이터 — 문서 일련번호로 월마감 리포트 조회. display.source = 리포트 원문.
 * 에러: 404=원천에 없는 일련번호 / 408=조회 시간 초과 / 422=sequence 누락·길이 초과 / 424=원천 응답에 data 없음
 */
export const getInfluencerMonthlyReportDraftSourceUrl = (sequence: string) =>
  `${APPROVAL_V2_URL}/type/108/draft-source?${new URLSearchParams({
    sequence,
  }).toString()}`;

/**
 * 인플루언서 PPL 정산(135) 작성 사전 데이터 — 문서 일련번호로 인플루언서 시스템 PPL 정산 리포트 조회. display.source = 리포트 원문.
 * 에러: 404=원천에 없는 일련번호(dev 는 500 으로 옴) / 408=시간 초과 / 422=일련번호 누락·길이 초과 / 424=원천 응답에 data 없음
 */
export const getInfluencerPplDraftSourceUrl = (sequence: string) =>
  `${APPROVAL_V2_URL}/type/135/draft-source?${new URLSearchParams({
    sequence,
  }).toString()}`;

/**
 * SAP 예산증액 신청(187) 작성 사전 데이터 — 증액 연월(YYYY-MM)의 작성자 계정별 예산. 조회 주체는 서버가 요청자로 고정.
 * 에러: 422=연월 형식 오류·작성자 ERP 계정 없음 / 424=SAP 조회 실패
 */
export const getSapBudgetIncreaseDraftSourceUrl = (budgetDate: string) =>
  `${APPROVAL_V2_URL}/type/187/draft-source?${new URLSearchParams({
    budgetDate,
  }).toString()}`;

/**
 * (구)매장 OPEN 품의(8) 작성 사전 데이터 — storeKind 만 보내면 매장 후보, 후보 키 3개를 더 보내면 프리필·매장 정보·인테리어 예상비용·상세 내역.
 * 에러: 422=후보에 없는 키 / 424=ERP 조회 실패
 */
export const getStoreOpenDraftSourceUrl = (
  storeKind: 'department' | 'direct',
  selected?: { brandCode: string; season: string; departmentDocId: string },
) =>
  `${APPROVAL_V2_URL}/type/8/draft-source?${new URLSearchParams({
    storeKind,
    ...selected,
  }).toString()}`;

/**
 * (구)매장 OPEN 품의(8) 저장 문서의 인테리어 비용 상세 — 저장 시점이 아닌 지금 원천 값.
 * 에러: 403=열람 불가 / 404=대리점 문서 / 424=ERP 조회 실패
 */
export const getStoreOpenInteriorDetailUrl = (documentId: string) =>
  `${APPROVAL_V2_URL}/${encodeURIComponent(
    documentId,
  )}/store-open/interior-detail`;

/**
 * 양식별 작성 가이드. 작성자 법인(companyCode) 가이드가 있으면 그것, 없으면 공통 가이드.
 * 어느 쪽이 내려왔는지는 응답 `source` 로 구분.
 */
export const getApprovalTypeGuideUrl = (code: number, companyCode: string) =>
  `${APPROVAL_V2_URL}/type/${code}/guide?companyCode=${encodeURIComponent(
    companyCode,
  )}`;

export const LEGACY_APPROVAL_SEARCH_URL = '/api/v1/legacy-approval';

export const getApprovalBookmarkUrl = (documentId: string) =>
  `${APPROVAL_V2_URL}/${documentId}/bookmark`;

// 연관품의 (parents/children/related)
export const getApprovalRelationsUrl = (documentId: string) =>
  `${APPROVAL_V2_URL}/${documentId}/relations`;

export const getApprovalPortalDataUrl = (
  documentId: string,
  ignoreDocumentIds?: string[],
) => {
  const base = `${LEGACY_APPROVAL_SEARCH_URL}/${documentId}`;
  if (!ignoreDocumentIds?.length) return base;
  return `${base}?ignoreDocumentIds=${encodeURIComponent(
    ignoreDocumentIds.join(','),
  )}`;
};

/**
 * 품의 AI 요약 결과는 v2 단건조회(`${APPROVAL_V2_URL}/{documentId}`)의 `ai` 로 받음.
 * v1 `/legacy-approval/{documentId}/summary` 는 v2 요약 요청 결과가 반영되지 않아 쓰지 않음.
 *
 * SSE 토픽은 `approval:{documentId}` (useSSE 형식). 이벤트 5종:
 *   approval.summarize.{start,summary.start,summary.complete,complete,error}
 */

export const COMBINED_DEPARTMENT_URL = '/api/v1/legacy-approval-department';

// 품의 작성/제출 (v2 namespace — legacy-approval 과 별도)
export const APPROVAL_V2_URL = '/api/v2/approval';

export const APPROVAL_SEARCH_URL = IS_APPROVAL_SEARCH_V2_ENABLED
  ? APPROVAL_V2_URL
  : LEGACY_APPROVAL_SEARCH_URL;

/**
 * 목록 계열 캐시 키 판정(legacy-approval 하위 뱃지 등 포함). V2 검색은 단건 조회
 * `/api/v2/approval/{id}` 와 경로가 겹쳐 쿼리 시작까지 봄.
 */
export const isApprovalListKey = (url: string) =>
  url.startsWith(LEGACY_APPROVAL_SEARCH_URL) ||
  (IS_APPROVAL_SEARCH_V2_ENABLED &&
    (url === APPROVAL_SEARCH_URL || url.startsWith(`${APPROVAL_SEARCH_URL}?`)));

/**
 * 계약 품의(117·118) AI 초안 생성 — V2(PORTAL-14185). body { contractIds: number[] }.
 * 이미 생성된 계약은 drafts 로 즉시, 미생성은 pending 으로 돌아오고 완료는 SSE 토픽
 * PROJECT_CONTRACT_DRAFT_TOPIC 의 draft_ready / draft_error 로 계약별 통지.
 * failed 는 파이프라인 시작 실패(재요청 시 재시도). 완료 후 같은 계약 재호출은 drafts 즉시 —
 * SSE 를 놓쳤을 때 재호출이 곧 폴링. 레거시 v1 /api/v1/approval/ai/project-contract/async 와 별개
 */
export const APPROVAL_PROJECT_CONTRACT_DRAFT_URL = `${APPROVAL_V2_URL}/project-contract/draft`;

export const getProjectContractDraftTopic = (passportId: string) =>
  `project-contract-draft:${passportId}`;

// 어드민 전용 v2 네임스페이스 — 역할과 별개로 서버가 유형별 관리 권한을 따로 검사(403)
export const APPROVAL_V2_CONTROL_URL = '/api/v2/control/approval';

/**
 * 양식 작성 본문 템플릿 저장(어드민) — PUT `{ contentTemplate: string | null }`, 204.
 * companyCode 를 받지 않아 법인 구분 없이 전역 저장. 제목·결재라인 등 다른 필드는 저장 대상 아님.
 * 읽기는 `getApprovalTypeUrl` 응답의 contentTemplate — 저장 후 그 캐시를 무효화할 것.
 */
export const getApprovalTypeTemplateUrl = (code: number) =>
  `${APPROVAL_V2_CONTROL_URL}/type/${code}/template`;

/**
 * 근태 자가결재 품의의 조회 본문 데이터(v1). v2 단건조회 attributes 에는 신청 구간만 있고
 * 현황(주간·월간 연장근무, 잔여 시간, 변경 전 근무시간)은 여기에만 있어 조회 화면이 따로 읽음.
 * 레거시 조회 폼(appr_view_forms_hr_*)이 PHP 프록시로 부르던 것과 같은 엔드포인트.
 */
export const getWorkingHoursHistoryUrl = (documentId: string) =>
  `/api/v1/passport/working-hours/histories/${documentId}`;
export const getOvertimeApplyUrl = (documentId: string) =>
  `/api/v1/passport/overtime/${documentId}`;
export const getToilTimeAdjustmentUrl = (documentId: string) =>
  `/api/v1/passport/toil-time-adjustment/${documentId}`;
export const getAnnualLeaveUrl = (documentId: string) =>
  `/api/v1/passport/annual-leave/${documentId}`;

/**
 * 이어서 결재할 문서 1건 — 선정(내 차례인 진행중 품의 / 현재 문서 제외 / 기안일 현재 문서
 * 이하 중 최신)은 서버 몫. 신·구 화면 상신분을 함께 봄. 없으면 data=null. 양식 코드는 미포함.
 */
export const getNextConfirmApprovalUrl = (documentId: string) =>
  `${APPROVAL_V2_URL}/${documentId}/next-confirm`;

/**
 * 결재라인에 표시되는 인물(작성자·결재자·참조자)의 현재 재직·휴직 상태.
 * 상신 시점에 박제된 결재라인과 달리 조회 시점의 프로필을 읽어 판정하므로,
 * 오래된 문서에서도 지금의 퇴사·휴직 여부가 내려온다.
 * 상세 조회 응답의 인물 항목과 intranetId(겸직 대비 passportId 까지)로 매칭한다.
 */
export const getApprovalLineMembersUrl = (documentId: string) =>
  `${APPROVAL_V2_URL}/${documentId}/line-members`;

/**
 * 결재 AI 어시스턴트 채팅 (PORTAL-15493).
 * POST chat → 202 {sessionId, topic, aiPipelineId}. 답변은 SSE 토픽 `approval-chat:{sessionId}` 의
 * `approval.chat.answer.complete | error` 로 받고, 이력은 sessions / messages 로 복원한다.
 */
export const getApprovalChatUrl = (documentId: string) =>
  `${APPROVAL_V2_URL}/${documentId}/chat`;

export const getApprovalChatSessionsUrl = (documentId: string) =>
  `${APPROVAL_V2_URL}/${documentId}/chat/sessions`;

export const getApprovalChatMessagesUrl = (
  documentId: string,
  sessionId: string,
) => `${APPROVAL_V2_URL}/${documentId}/chat/sessions/${sessionId}/messages`;

// 제안 카드 응답 기록 — PATCH {state: 'accepted' | 'declined'}
export const getApprovalChatSuggestionUrl = (
  documentId: string,
  sessionId: string,
  messageId: string,
) =>
  `${APPROVAL_V2_URL}/${documentId}/chat/sessions/${sessionId}/messages/${messageId}/suggestion`;

// 최근 결재라인(작성자 본인의 최근 결재 문서 + 결재자/협조/참조 동반) — 결재라인 패널
// 쿼리: type(양식 코드 필터), completed(true=완료 문서만), listSize, currentPage
export const APPROVAL_V2_RECENT_APPROVERS_URL = `${APPROVAL_V2_URL}/recent-approvers`;

/**
 * AI 요약 재요청(POST) — v2. 결과는 v2 단건조회 ai.
 *  - POST `/api/v2/approval/{documentId}/summarize`
 *  - 응답 → `{ aiPipelineId: string | null }` (null = 본문 비어있음 = skipped)
 */
export const getApprovalSummarizeRequestUrl = (documentId: string) =>
  `${APPROVAL_V2_URL}/${documentId}/summarize`;

// 히스토리 기반
const HISTORY_BASE_URL = '/api/v1/history';

export const LEGACY_APPROVAL_RECENT_READ = `${HISTORY_BASE_URL}/approval/viewed/recently`;
export const APPROVAL_RECOMMEND_FILTER_URL = `${HISTORY_BASE_URL}/approval/filter/recommend`;
export const APPROVAL_RECENT_SEARCH_KEYWORD = `${HISTORY_BASE_URL}/approval/filter/keyword`;

// 연관품의 변경이력 — GET /api/v1/history/approval/relation/{docid}
// 응답 항목은 PHP `getRelationHistory` 와 동일 형태(append-only 로그).
export const getRelationHistoryUrl = (documentId: string) =>
  `${HISTORY_BASE_URL}/approval/relation/${documentId}`;

// 즐겨찾기 필터
export const APPROVAL_FAVORITE_FILTER = `${LEGACY_APPROVAL_SEARCH_URL}/favorite-filter`;

// AI 번역 — 번역 가능한 언어 목록 (레거시 getAITranslateList 이관).
// 응답 shape: { data: AITranslateLanguage[] }.
export const AI_TRANSLATE_LANGUAGES_URL =
  '/api/v1/labs/translation/advanced/languages';

// AI 번역 — 단건 문서 번역 실행.
// POST /api/v1/legacy-approval/{documentId}/translation?sourceLanguage=&targetLanguage=
// 응답 shape: { data: { documentId, targetLanguage, translatedSubject, translatedContent } }.
export const getLegacyApprovalTranslationUrl = (documentId: string) =>
  `${LEGACY_APPROVAL_SEARCH_URL}/${documentId}/translation`;

// 마케팅 AI 분석
export const APPROVAL_MARKETING_AI_PARAMETERS =
  '/api/v1/ai/marketing-expense-approval/parameters';

export const APPROVAL_MARKETING_AI_SUMMARY =
  '/api/v1/ai/marketing-expense-approval/summary';

export const APPROVAL_MARKETING_AI_ANALYSIS =
  '/api/v1/ai/marketing-expense-approval/analysis';

export const APPROVAL_MARKETING_ANALYSIS =
  '/api/v1/approval/marketing-analysis';

// F&CO 마케팅 사전 품의(221) 작성 화면 — GET /{analysisId} 는 v1 과 같은 응답(data.result.value)
export const APPROVAL_MARKETING_ANALYSIS_V2 = `${APPROVAL_V2_URL}/marketing-analysis`;

/**
 * 마케팅 비용 품의(212) 작성 사전 데이터 — data.display.source 가 분석 레코드 원문, display.subject 가 제목 기본값.
 * 에러: 404=없는 analysisId / 422=analysisId 누락
 */
export const getMarketingExpenseDraftSourceUrl = (analysisId: string) =>
  `${APPROVAL_V2_URL}/type/212/draft-source?analysisId=${encodeURIComponent(
    analysisId,
  )}`;

/**
 * (구)마케팅 건별 진행(83) 작성 사전 데이터 — 키 없이 부르면 진행 대기 건 후보, 키 4개를 주면 그 건 상세까지.
 * 에러: 422=선택 키가 진행 대기 목록에 없음(저장한 건은 다시 못 부름) / 424=ERP 조회 실패
 */
/** GERP PLM(123) — itemIds 없으면 내 미상신 목록, 있으면 그 건들 본문(CENTRIC 은 styleId, NEW_PLM 은 aprvId) */
export const getProductLifecycleDraftSourceUrl = (
  plmType: string,
  itemIds?: string[],
) => {
  const params = new URLSearchParams({ plmType });
  if (itemIds?.length) params.set('itemIds', itemIds.join(','));
  return `${APPROVAL_V2_URL}/type/123/draft-source?${params.toString()}`;
};

/** GERP PLM(123) 작성 중 품번의 상세내역 파일 — 서버가 내 미상신 목록에서 다시 찾으므로 상신된 건은 못 받음 */
export const getProductLifecycleFileUrl = (plmType: string, itemId: string) =>
  `${APPROVAL_V2_URL}/type/123/draft-source/file?${new URLSearchParams({
    plmType,
    itemId,
  }).toString()}`;

export const getMarketingCaseDraftSourceUrl = (key?: {
  brandCode: string;
  season: string;
  costDocId: string;
  approvalDate: string;
}) => {
  const base = `${APPROVAL_V2_URL}/type/83/draft-source`;
  return key ? `${base}?${new URLSearchParams(key).toString()}` : base;
};

/** GERP 생산거래처 신규/변경(120) — approvalId 없으면 내 미상신 목록, 있으면 그 건 본문 */
export const getProductionVendorDraftSourceUrl = (approvalId?: string) => {
  const base = `${APPROVAL_V2_URL}/type/120/draft-source`;
  return approvalId
    ? `${base}?${new URLSearchParams({ approvalId }).toString()}`
    : base;
};

/** GERP 저장부자재 발주(125) — approvalId 없으면 내 미상신 목록, 있으면 그 건 본문 */
export const getStorageMaterialOrderDraftSourceUrl = (approvalId?: string) => {
  const base = `${APPROVAL_V2_URL}/type/125/draft-source`;
  return approvalId
    ? `${base}?${new URLSearchParams({ approvalId }).toString()}`
    : base;
};

/**
 * GERP 생산비 마감(124) — yyyymm 필수. approvalId 없으면 그 달 미상신 목록, 있으면 그 건 본문,
 * invId 까지 주면 그 행 상세(display.detail)
 */
export const getProductionCostClosingDraftSourceUrl = (params: {
  yyyymm: string;
  approvalId?: string;
  invId?: string;
}) => {
  const query = new URLSearchParams({ yyyymm: params.yyyymm });
  if (params.approvalId) query.set('approvalId', params.approvalId);
  if (params.invId) query.set('invId', params.invId);
  return `${APPROVAL_V2_URL}/type/124/draft-source?${query.toString()}`;
};

/** GERP 생산비 마감(124)·클레임 마감(186) 저장된 문서의 행 상세보기 — GERP 현재 값 */
export const getGerpProductionDetailUrl = (
  documentId: string,
  params: { approvalId: string; invId?: string },
) => {
  const query = new URLSearchParams({ approvalId: params.approvalId });
  if (params.invId) query.set('invId', params.invId);
  return `${APPROVAL_V2_URL}/${encodeURIComponent(
    documentId,
  )}/gerp-detail?${query.toString()}`;
};

/** 문서에 묶인 GERP 결재 건과 현재 결재 상태 */
export const getApprovalItemsUrl = (documentId: string) =>
  `${APPROVAL_V2_URL}/${encodeURIComponent(documentId)}/items`;

/** GERP 클레임 마감(186) — approvalIds 없으면 내 미상신 목록, 있으면 그 건들 카드. detailApprovalId 를 주면 그 건 송장 라인까지 */
export const getClaimClosingDraftSourceUrl = (
  approvalIds?: string[],
  detailApprovalId?: string,
) => {
  const base = `${APPROVAL_V2_URL}/type/186/draft-source`;
  if (!approvalIds?.length) return base;
  const query = new URLSearchParams({ approvalId: approvalIds.join(',') });
  if (detailApprovalId) query.set('detailApprovalId', detailApprovalId);
  return `${base}?${query.toString()}`;
};

/** GERP 협력사 평가(127) — approvalId 없으면 내 미상신 목록, 있으면 그 건 본문 */
export const getSupplierEvaluationDraftSourceUrl = (approvalId?: string) => {
  const base = `${APPROVAL_V2_URL}/type/127/draft-source`;
  return approvalId
    ? `${base}?${new URLSearchParams({ approvalId }).toString()}`
    : base;
};

/** GERP STYLE MASTER 가격 변경(128) — approvalIds 없으면 내 미상신 목록, 있으면 그 건들 본문 */
export const getStyleMasterPriceDraftSourceUrl = (approvalIds?: string[]) => {
  const base = `${APPROVAL_V2_URL}/type/128/draft-source`;
  return approvalIds?.length
    ? `${base}?${new URLSearchParams({
        approvalId: approvalIds.join(','),
      }).toString()}`
    : base;
};

/** GERP 저장품 추가발주(184) — approvalIds 없으면 내 미상신 목록, 있으면 그 건들 카드 */
export const getInventoryReplenishmentDraftSourceUrl = (
  approvalIds?: string[],
) => {
  const base = `${APPROVAL_V2_URL}/type/184/draft-source`;
  return approvalIds?.length
    ? `${base}?${new URLSearchParams({
        approvalId: approvalIds.join(','),
      }).toString()}`
    : base;
};

/** GERP PR(제상품)(122)·PO(합의납기일/생산처)(129) — approvalId 없으면 내 미상신 목록, 있으면 그 건들 본문(여러 건은 쉼표) */
export const getPurchaseRequisitionDraftSourceUrl = (
  approvalIds?: string[],
  apvType: 122 | 129 = 122,
) => {
  const base = `${APPROVAL_V2_URL}/type/${apvType}/draft-source`;
  return approvalIds?.length
    ? `${base}?${new URLSearchParams({
        approvalId: approvalIds.join(','),
      }).toString()}`
    : base;
};
/** GERP 원가 견적서(121) — approvalId 없으면 내 미상신 목록, 있으면 그 건들 본문(여러 건은 쉼표) */
export const getCostQuotationDraftSourceUrl = (approvalIds?: string[]) => {
  const base = `${APPROVAL_V2_URL}/type/121/draft-source`;
  return approvalIds?.length
    ? `${base}?${new URLSearchParams({
        approvalId: approvalIds.join(','),
      }).toString()}`
    : base;
};

/**
 * GERP CLAIM NOTE(166) — 인자 없으면 클레임 유형만, howlist·gubun 이 있으면 미상신 목록,
 * approvalIds 까지 있으면 그 건들 카드
 */
export const getClaimNoteDraftSourceUrl = (params?: {
  howlist: string;
  gubun: string;
  approvalIds?: string[];
}) => {
  const base = `${APPROVAL_V2_URL}/type/166/draft-source`;
  if (!params) return base;
  const query = new URLSearchParams({
    howlist: params.howlist,
    gubun: params.gubun,
  });
  if (params.approvalIds?.length)
    query.set('approvalId', params.approvalIds.join(','));
  return `${base}?${query.toString()}`;
};

/** GERP Sales Contract(136) — approvalIds 없으면 내 미상신 목록, 있으면 그 건들 카드 */
export const getSalesContractDraftSourceUrl = (approvalIds?: string[]) => {
  const base = `${APPROVAL_V2_URL}/type/136/draft-source`;
  return approvalIds?.length
    ? `${base}?${new URLSearchParams({
        approvalId: approvalIds.join(','),
      }).toString()}`
    : base;
};

/** GERP CREDIT NOTE(156) — approvalIds 없으면 내 미상신 목록, 있으면 그 건들 카드 */
export const getCreditNoteDraftSourceUrl = (approvalIds?: string[]) => {
  const base = `${APPROVAL_V2_URL}/type/156/draft-source`;
  return approvalIds?.length
    ? `${base}?${new URLSearchParams({
        approvalId: approvalIds.join(','),
      }).toString()}`
    : base;
};

// 인테리어 공사비 적정성 보고서 (PORTAL-12794)
export const APPROVAL_INTERIOR_COST_ADEQUACY =
  '/api/v1/approval/interior-cost-adequacy';

// 인테리어 정산 품의(234) 결재 문서 저장 (PORTAL-14089) — 위와 동일 계약, 정산 전용 리소스.
//   POST {storeId?, docId?, result} · GET ?storeId= (매장당 0~1건) · GET /{id} · PUT /{id}
//   오픈 문서와 완전히 독립(같은 매장에 각각 1건) — storeId upsert 가 오픈 문서를 덮어쓰지 않는다.
export const APPROVAL_INTERIOR_SETTLEMENT =
  '/api/v1/approval/interior-settlement';

// 사전 비용 품의(205) AI 견적서·영수증 추출 — POST {files:[{url,mimeType}]} → data.id, GET /{aiPipelineId} → data.result[]
//   완료·실패는 SSE approval-export:{passportId} 의 approval.export.extract.* (documentType 'expense')
export const APPROVAL_AI_EXPENSE_EXTRACTION =
  '/api/v1/approval/ai/expense-approval/async';

// 품의 작성 사전 데이터 AI 추출(V2, supportsAiDraftSource 유형) — POST {files:[{fileUuid,mimeType}]} → data.aiPipelineId,
//   GET /{aiPipelineId} → data.display.results[]. 완료·실패는 SSE approval-export:{passportId}
export const getApprovalAiDraftSourceUrl = (code: number) =>
  `${APPROVAL_V2_URL}/type/${code}/ai-draft-source`;

// ENT. 프로젝트(비용) 품의(213) SAP 조회 — 둘 다 data[] (KOSTL·LTEXT / Kstar·KstarText·BudgetTotl·Remain)
export const getSapCostCenterUrl = (sapCompanyCode: string) =>
  `/api/v1/labs/sap/cost-center/${sapCompanyCode}`;
export const SAP_ENTERTAINMENT_BUDGET_URL =
  '/api/v1/labs/sap/entertainment/marketing/budget';

// 첨부 — 업로드 제약
export const ATTACHMENT_MAX_SIZE = 500 * 1024 * 1024; // 500MB
export const AI_ATTACHMENT_MAX_SIZE = 20 * 1024 * 1024; // 20MB (레거시 AI 업로더 안내문 기준)
export const ATTACHMENT_FILENAME_MAX_LENGTH = 200;
// 신규입사자 법인카드(204) 신분증·통장 다운로드 유효시간 7일 — 서버 최대값
export const ATTACHMENT_PRIVATE_DOWNLOAD_EXPIRATION_SECONDS = 7 * 24 * 60 * 60;

// 작성 폼 — 제목 글자수 제한 (v2 정책. 레거시 90자에서 변경)
export const SUBJECT_MAX_LENGTH = 200;

/**
 * 반려요청 사유 글자수 제한 — 서버 검증과 같은 값(PORTAL-14138).
 *
 * 사유는 레거시 오라클 컬럼 `APV_HEAD.REQ_RETURN_REASON`(VARCHAR2 1000, BYTE 세만틱)에 그대로 저장된다.
 * 서버는 300자 + 1000바이트 두 겹으로 막는다 — 한글 300자는 900바이트지만 이모지(1자 4바이트)는
 * 300자 이하로도 컬럼을 넘겨 ORA-12899 가 나기 때문이다. 화면은 글자 수만 막고, 바이트 초과는
 * 서버가 422 안내로 돌려준다(입력 중 바이트를 세어 막지는 않는다).
 */
export const RETURN_REQUEST_REASON_MAX_LENGTH = 300;

export const AI_ALLOWED_EXTENSIONS = [
  'pdf',
  'xls',
  'xlsx',
  'csv',
  'png',
  'jpg',
  'jpeg',
  'jfif',
  'doc',
  'docx',
  'webp',
];

/** 매장 OPEN(233) 리포트 업로더 — html 리포트만(레거시 allowedExtensions ['html']) */
export const AI_REPORT_ALLOWED_EXTENSIONS = ['html'];

export const AI_VERIFIER_ALLOWED_EXTENSIONS = [
  'pdf',
  'png',
  'jpg',
  'jpeg',
  'jfif',
  'webp',
];

export const ATTACHMENT_UPLOAD_VARIANT_CONFIG: Record<
  AttachmentUploadVariant,
  AttachmentUploadVariantConfig
> = {
  default: {
    accept: '',
    allowedExtensions: new Set<string>(),
    multiple: true,
    maxSize: ATTACHMENT_MAX_SIZE,
    downloadType: 'public',
  },
  editor: {
    accept: '',
    allowedExtensions: new Set<string>(),
    multiple: true,
    maxSize: ATTACHMENT_MAX_SIZE,
    downloadType: 'public',
  },
  ai: {
    accept: AI_ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(','),
    allowedExtensions: new Set(AI_ALLOWED_EXTENSIONS),
    multiple: false,
    maxSize: AI_ATTACHMENT_MAX_SIZE,
    downloadType: 'public',
  },
  'ai-report': {
    accept: AI_REPORT_ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(','),
    allowedExtensions: new Set(AI_REPORT_ALLOWED_EXTENSIONS),
    multiple: false,
    maxSize: AI_ATTACHMENT_MAX_SIZE,
    downloadType: 'public',
  },
  'ai-verifier': {
    accept: AI_VERIFIER_ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(','),
    allowedExtensions: new Set(AI_VERIFIER_ALLOWED_EXTENSIONS),
    multiple: false,
    maxSize: ATTACHMENT_MAX_SIZE,
    downloadType: 'private',
    downloadExpirationSeconds: ATTACHMENT_PRIVATE_DOWNLOAD_EXPIRATION_SECONDS,
  },
  'ai-ent-expense': {
    accept: AI_VERIFIER_ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(','),
    allowedExtensions: new Set(AI_VERIFIER_ALLOWED_EXTENSIONS),
    multiple: false,
    maxSize: ATTACHMENT_MAX_SIZE,
    downloadType: 'public',
  },
};
